import { useState, useMemo, Component, ErrorInfo, useEffect } from "react";
import type { ReactNode } from "react";
import { useParams, Link, useLocation, useSearch } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { format } from "date-fns";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/page-header";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  ArrowLeft, 
  Calendar, 
  MapPin, 
  FileText, 
  User, 
  Clock,
  ExternalLink,
  MessageSquare,
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
  Activity,
  MoreHorizontal,
  Trash2,
  Search,
  X,
  Mail,
  Pencil,
} from "lucide-react";
import { formatReportDate } from "@/lib/utils";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { BehaviorDiagnosticsDrawer } from "@/components/behavior-diagnostics-drawer";
import { usePageTitle } from "@/hooks/use-page-title";
import { useZoneLabels } from "@/hooks/use-zone-labels";

interface BehaviorInteraction {
  id: string;
  animalId: string;
  animalName: string | null;
  locationCode: string;
  zoneCode: string | null;
  eventType: string | null;
  scoreCode: string | null;
  scoreBand: string | null;
  occurredAt: string;
  author: string | null;
  rawNote: string | null;
  createdAt: string;
}

interface BehaviorReportDetailData {
  report: {
    id: string;
    reportDate: string;
    locationCode: string;
    sourceType: string;
    filename: string | null;
    filePath: string | null;
    pdfStorageProvider: string | null;
    uploadedAt: string | null;
    uploadedBy: string | null;
    createdAt: string;
    diagnostics: {
      totalEvents?: number;
      uniqueAnimals?: number;
      parseWarnings?: string[];
    } | null;
    deletedAt: string | null;
    uploader: {
      id: string;
      username: string;
      firstName: string | null;
      lastName: string | null;
    } | null;
    // I9.2: Provenance tracking
    sourceEmailId?: string | null;
  };
  interactions: BehaviorInteraction[];
}

// Error Boundary to catch React rendering errors
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("BehaviorReportDetail Error:", error);
    console.error("Component Stack:", errorInfo.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="container py-6">
          <Card>
            <CardContent className="py-12 text-center">
              <h2 className="text-lg font-semibold mb-2 text-destructive">Something went wrong</h2>
              <p className="text-muted-foreground mb-4">
                {this.state.error?.message || "An error occurred while loading this page."}
              </p>
              <pre className="text-xs text-left bg-muted p-4 rounded overflow-auto max-h-48">
                {this.state.error?.stack}
              </pre>
            </CardContent>
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}

function getScoreBandVariant(scoreBand: string | null): "default" | "secondary" | "destructive" | "outline" {
  switch (scoreBand) {
    case "GREEN":
      return "default";
    case "YELLOW":
      return "secondary";
    case "RED":
      return "destructive";
    default:
      return "outline";
  }
}

function getScoreBandIcon(scoreBand: string | null) {
  switch (scoreBand) {
    case "GREEN":
      return <CheckCircle2 className="h-3.5 w-3.5" />;
    case "YELLOW":
      return <AlertTriangle className="h-3.5 w-3.5" />;
    case "RED":
      return <AlertCircle className="h-3.5 w-3.5" />;
    default:
      return null;
  }
}

function formatTimeAsStored(isoString: string | null | undefined): string {
  // Format time as stored in database (UTC) without timezone conversion
  // This preserves the original local time from the PDF
  if (!isoString) return "—";
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "—";
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours % 12 || 12;
    return `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  } catch {
    return "—";
  }
}

function InteractionCard({ interaction }: { interaction: BehaviorInteraction }) {
  const { resolveLabel } = useZoneLabels();
  return (
    <Card 
      className="overflow-hidden"
      data-testid={`card-interaction-${interaction.id}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Link 
              href={`/animals/${interaction.animalId}`}
              className="hover:underline"
              data-testid={`link-animal-${interaction.animalId}`}
            >
              <span className="font-semibold text-primary">
                {interaction.animalName || interaction.animalId}
              </span>
            </Link>
            {interaction.scoreBand && (
              <Badge 
                variant={getScoreBandVariant(interaction.scoreBand)} 
                className="gap-1"
                data-testid={`badge-score-${interaction.id}`}
              >
                {getScoreBandIcon(interaction.scoreBand)}
                {interaction.scoreCode || interaction.scoreBand}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {interaction.zoneCode && (
              <Badge variant="outline" className="gap-1">
                <MapPin className="h-3 w-3" />
                {resolveLabel(interaction.zoneCode)}
              </Badge>
            )}
            {interaction.eventType && (
              <Badge variant="outline">
                {interaction.eventType}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1 flex-wrap">
          {interaction.animalName && (
            <span className="text-muted-foreground text-xs">
              {interaction.animalId}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatTimeAsStored(interaction.occurredAt)}
          </span>
          {interaction.author && (
            <span className="flex items-center gap-1">
              <User className="h-3 w-3" />
              {interaction.author}
            </span>
          )}
        </div>
      </CardHeader>
      {interaction.rawNote && (
        <CardContent className="pt-2">
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
            {interaction.rawNote}
          </p>
        </CardContent>
      )}
    </Card>
  );
}

export default function BehaviorReportDetail() {
  usePageTitle("Behavior Report");
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const search = useSearch();
  const { user } = useAuth();
  const { toast } = useToast();
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);

  useEffect(() => {
    if (new URLSearchParams(search).get("drawer") === "diagnostics") {
      setDiagnosticsOpen(true);
    }
  }, [search]);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<string>("animal");
  const [selectedScoreBand, setSelectedScoreBand] = useState<string>("all");

  const { data, isLoading, error } = useQuery<BehaviorReportDetailData>({
    queryKey: ["/api/behavior/reports", id],
    enabled: !!id,
  });

  const interactions = data?.interactions || [];

  const hasActiveFilters = searchQuery !== "" || selectedScoreBand !== "all";

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedScoreBand("all");
  };

  const filteredAndSortedInteractions = useMemo(() => {
    let filtered = interactions.filter((interaction) => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery ||
        (interaction.animalName && interaction.animalName.toLowerCase().includes(searchLower)) ||
        (interaction.animalId && interaction.animalId.toLowerCase().includes(searchLower)) ||
        (interaction.rawNote && interaction.rawNote.toLowerCase().includes(searchLower)) ||
        (interaction.author && interaction.author.toLowerCase().includes(searchLower));

      const matchesScoreBand = selectedScoreBand === "all" || 
        (selectedScoreBand === "UNSCORED" ? !interaction.scoreCode : interaction.scoreCode === selectedScoreBand);

      return matchesSearch && matchesScoreBand;
    });

    filtered.sort((a, b) => {
      if (sortBy === "time") {
        const timeA = a.occurredAt ? new Date(a.occurredAt).getTime() : 0;
        const timeB = b.occurredAt ? new Date(b.occurredAt).getTime() : 0;
        return timeA - timeB;
      } else if (sortBy === "animal") {
        const nameA = (a.animalName || a.animalId || "").toLowerCase();
        const nameB = (b.animalName || b.animalId || "").toLowerCase();
        const nameCompare = nameA.localeCompare(nameB);
        if (nameCompare !== 0) return nameCompare;
        const timeA = a.occurredAt ? new Date(a.occurredAt).getTime() : 0;
        const timeB = b.occurredAt ? new Date(b.occurredAt).getTime() : 0;
        return timeA - timeB;
      }
      return 0;
    });

    return filtered;
  }, [interactions, searchQuery, selectedScoreBand, sortBy]);

  // Count interactions by actual score code - moved before early returns to fix hooks order
  const scoreCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    interactions.forEach(i => {
      if (i.scoreCode) {
        counts[i.scoreCode] = (counts[i.scoreCode] || 0) + 1;
      }
    });
    return Object.entries(counts).sort((a, b) => {
      const numA = parseInt(a[0]);
      const numB = parseInt(b[0]);
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      return a[0].localeCompare(b[0]);
    });
  }, [interactions]);


  const deleteMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/behavior/reports/${id}/delete`),
    onSuccess: () => {
      toast({
        title: "Report deleted",
        description: "Report removed. Unreviewed data from this report cleared. Associated behavior interactions excluded from queries.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/behavior/reports"] });
      setLocation("/reports");
    },
    onError: (error: Error) => {
      toast({
        title: "Delete failed",
        description: error.message || "Failed to delete the report.",
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <div className="container py-6 space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-9 w-9" />
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="grid gap-4 md:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="container py-6">
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <h2 className="text-lg font-semibold mb-2">Report Not Found</h2>
            <p className="text-muted-foreground mb-4">
              The interaction report you're looking for doesn't exist or has been deleted.
            </p>
            <Button asChild>
              <Link href="/reports" data-testid="link-back-reports">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Reports
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const report = data.report;
  
  // Defensive check for report date
  const safeReportDate = report.reportDate || new Date().toISOString().split('T')[0];
  
  const uploaderName = report.uploader 
    ? (report.uploader.firstName && report.uploader.lastName 
        ? `${report.uploader.firstName} ${report.uploader.lastName}` 
        : report.uploader.username)
    : "Unknown";

  const scoredInteractions = interactions.filter(i => i.scoreCode);
  const unscoredInteractions = interactions.filter(i => !i.scoreCode);

  return (
    <ErrorBoundary>
    <div className="px-6 pb-6 pt-4">
      <PageHeader
        breadcrumb={[
          { label: "Reporting" },
          { label: "Reports", href: "/reports" },
          { label: "Interaction Reports", href: "/reports?tab=interactions" },
        ]}
        title={`Interaction Report – ${report.locationCode || "Unknown"} – ${formatReportDate(safeReportDate)}`}
        actions={
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="default"
                  data-testid="button-actions"
                >
                  Actions
                  <MoreHorizontal className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem 
                  onClick={() => setDiagnosticsOpen(true)}
                  data-testid="menu-item-diagnostics"
                >
                  <Activity className="h-4 w-4 mr-2" />
                  View diagnostics
                </DropdownMenuItem>
                {(report.filePath || report.pdfStorageProvider === "s3") && (
                  <DropdownMenuItem 
                    onClick={(e) => {
                      e.preventDefault();
                      const url = `/api/behavior/reports/${report.id}/file`;
                      window.open(url, "_blank", "noopener,noreferrer");
                    }}
                    data-testid="action-open-pdf"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Open PDF
                    <ExternalLink className="h-3 w-3 ml-auto" />
                  </DropdownMenuItem>
                )}
                {user?.isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setConfirmDeleteOpen(true)}
                      className="text-destructive focus:text-destructive"
                      data-testid="menu-item-delete"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete report
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      />

      <div className="space-y-6 mt-6">
      <div className="grid gap-4 md:grid-cols-5">
        <Card data-testid="card-date">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              Report Date
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold" data-testid="text-report-date">
              {formatReportDate(safeReportDate)}
            </p>
          </CardContent>
        </Card>

        <Card data-testid="card-location">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              Location
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Badge variant="outline" className="text-base" data-testid="badge-location">
              {report.locationCode}
            </Badge>
          </CardContent>
        </Card>

        <Card data-testid="card-interactions">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <MessageSquare className="h-4 w-4" />
              Interactions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold" data-testid="text-interaction-count">
              {interactions.length}
            </p>
          </CardContent>
        </Card>

        <Card data-testid="card-uploader">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <User className="h-4 w-4" />
              Uploaded By
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm font-medium" data-testid="text-uploader">
              {uploaderName}
            </p>
            <p className="text-xs text-muted-foreground">
              {(() => {
                try {
                  const dateStr = report.uploadedAt || report.createdAt;
                  return dateStr ? format(new Date(dateStr), "MMM d, yyyy h:mm a") : "—";
                } catch {
                  return "—";
                }
              })()}
            </p>
          </CardContent>
        </Card>

        <Card data-testid="card-source">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              {report.sourceEmailId ? <Mail className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
              Source
            </CardDescription>
          </CardHeader>
          <CardContent>
            {report.sourceEmailId ? (
              <Badge 
                variant="outline" 
                className="gap-1 text-xs bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800"
                data-testid="badge-source-email"
              >
                <Mail className="h-3 w-3" />
                Email
              </Badge>
            ) : (
              <Badge 
                variant="outline" 
                className="gap-1 text-xs"
                data-testid="badge-source-manual"
              >
                <Pencil className="h-3 w-3" />
                Manual
              </Badge>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="space-y-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <CardTitle>
              Animals in Interaction Report
              {hasActiveFilters && (
                <span className="text-sm font-normal text-muted-foreground ml-2" data-testid="text-filtered-count">
                  (showing {filteredAndSortedInteractions.length} of {interactions.length})
                </span>
              )}
            </CardTitle>
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex-1 min-w-[200px] max-w-[300px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search interactions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="input-search-interactions"
                />
              </div>
            </div>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[140px]" data-testid="select-sort">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="time">By Time</SelectItem>
                <SelectItem value="animal">By Animal</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2 flex-wrap">
              {scoreCounts.map(([scoreCode, count]) => (
                <Badge 
                  key={scoreCode} 
                  variant={selectedScoreBand === scoreCode ? "default" : "outline"} 
                  className={`gap-1 cursor-pointer transition-colors ${selectedScoreBand === scoreCode ? "ring-2 ring-primary ring-offset-1" : "hover-elevate"}`}
                  onClick={() => setSelectedScoreBand(selectedScoreBand === scoreCode ? "all" : scoreCode)}
                  data-testid={`badge-score-filter-${scoreCode}`}
                >
                  Score {scoreCode}: {count}x
                </Badge>
              ))}
              {unscoredInteractions.length > 0 && (
                <Badge 
                  variant={selectedScoreBand === "UNSCORED" ? "default" : "outline"} 
                  className={`gap-1 cursor-pointer transition-colors ${selectedScoreBand === "UNSCORED" ? "ring-2 ring-primary ring-offset-1" : "hover-elevate"}`}
                  onClick={() => setSelectedScoreBand(selectedScoreBand === "UNSCORED" ? "all" : "UNSCORED")}
                  data-testid="badge-score-filter-unscored"
                >
                  Unscored: {unscoredInteractions.length}x
                </Badge>
              )}
            </div>

            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="gap-1"
                data-testid="button-clear-filters"
              >
                <X className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {interactions.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground" data-testid="empty-no-interactions">
              No interactions recorded in this report.
            </div>
          ) : filteredAndSortedInteractions.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground" data-testid="empty-no-matches">
              No interactions match the current filters
            </div>
          ) : (
            <div className="space-y-4">
              {filteredAndSortedInteractions.map((interaction) => (
                <InteractionCard key={interaction.id} interaction={interaction} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      </div>

      <BehaviorDiagnosticsDrawer
        open={diagnosticsOpen}
        onOpenChange={setDiagnosticsOpen}
        diagnostics={report.diagnostics}
        location={report.locationCode || "Unknown"}
        reportDate={formatReportDate(safeReportDate)}
        scoredCount={scoredInteractions.length}
        unscoredCount={unscoredInteractions.length}
        scoreCounts={scoreCounts}
      />

      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Interaction Report</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this interaction report? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </ErrorBoundary>
  );
}
